import {QueryResultType} from 'graphql-client/BenchlingQuery';
import useResolvedChipItemIdMap from 'insights/analytics-visualizations/chip-items/useResolvedChipItemIdMap';
import {RunQueryOutputLegacyData} from 'insights/warehouse/AnalyticsWarehouseTypes';
import cellToItemIds from 'insights/warehouse/cellToItemIds';
import {JSONValue} from 'util/Types';

import {ResolvedChipItemIdMap} from './ResolvedChipItemTypes';

/**
 * If the given cell contains chip item ids, return a comma-separated string of those items' names.
 * Otherwise, return cell unmodified.
 *
 * Example 1:
 *   cell = "prtn_1"
 *   resolvedChipItemIdMap = {prtn_1: "Protein 1"}
 *   returns "Protein 1"
 *
 * Example 2:
 *   cell = ["prtn_1", "prtn_2"]
 *   resolvedChipItemIdMap = {prtn_1: "Protein 1", prtn_2: "Protein 2"}
 *   returns "Protein 1, Protein 2"
 *
 * Example 3:
 *   cell = "prtn_1"
 *   resolvedChipItemIdMap = {}
 *   returns "prtn_1"
 *
 * Keep in sync with benchling/insights/analytics_visualizations/chip_items/resolve_run_query_output.py
 * resolve_cell
 */
export function resolveCell(cell: JSONValue, resolvedChipItemIdMap: ResolvedChipItemIdMap): JSONValue {
  const resolvedCellIds = cellToItemIds(cell, (stringValue) => resolvedChipItemIdMap[stringValue] != null);
  if (resolvedCellIds != null) {
    const names = resolvedCellIds.map((cellId) => resolvedChipItemIdMap[cellId]!.name);
    return names.join(', ');
  }
  return cell;
}

/**
 * Transform the input runQueryOutput with cells that have chip item ids replaced
 * with their chip item names using resolvedChipItemIdMap.
 *
 * Always does a lookup to resolvedChipItemIdMap. This is slightly inconsistent with
 * TableViewer, which enforces chipRangeEnd.
 *
 * Say these are the query results:
 *   prtn_0      prtn_1
 *   prtn_0      prtn_2 (over limit, chipRangeEnd = 1)
 *
 * TableViewer will render
 *   Protein 0*  Protein 1* (* chip)
 *   prtn_0      prtn_2
 *
 * withIdsAsNames will return
 *   Protein 0   Protein 1
 *   Protein 0   prtn_2
 *
 * Keep in sync with benchling/insights/analytics_visualizations/chip_items/resolve_run_query_output.py
 * _with_api_ids_as_names
 */
export function withIdsAsNames(
  runQueryOutput: RunQueryOutputLegacyData,
  resolvedChipItemIdMap: ResolvedChipItemIdMap
): RunQueryOutputLegacyData {
  return {
    ...runQueryOutput,
    rows: runQueryOutput.rows.map((row) => row.map((cell) => resolveCell(cell, resolvedChipItemIdMap))),
  };
}

export interface UseResolvedRunQueryOutputOutput {
  queryResultType: QueryResultType;
  resolvedRunQueryOutput: RunQueryOutputLegacyData;
}

/**
 * Replace chip item ids in runQueryOutput with resolved chip item names.
 *
 * Return resolvedRunQueryOutput identical to the input runQueryOutput except with cells that contain
 * chip item ids replaced with string representations of the corresponding chip item names,
 * and queryResultType, the status of the request to resolve the chip item ids to names.
 *
 * Keep in sync with benchling/insights/analytics_visualizations/chip_items/resolve_run_query_output.py
 */
export default function useResolvedRunQueryOutput(
  runQueryOutput: RunQueryOutputLegacyData,
  skipResolution: boolean = false
): UseResolvedRunQueryOutputOutput {
  const {queryResultType, resolvedChipItemIdMap} = useResolvedChipItemIdMap(runQueryOutput, skipResolution);
  return {
    queryResultType,
    resolvedRunQueryOutput: skipResolution
      ? runQueryOutput
      : withIdsAsNames(runQueryOutput, resolvedChipItemIdMap),
  };
}
